import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  API_SERVICE_CONFIG,
  IApiServiceConfig,
} from '../base/configuration/api-service-config';
import { WorkOrderAction, WorkOrderActionInput } from './work-order-action';
import { map, Observable } from 'rxjs';
import { isSingleErrorCode } from '@fullyops/shared/is-single-error-code';

@Injectable({ providedIn: 'root' })
export class ApiWorkOrderActionsAdapterService {
  constructor(
    private http: HttpClient,
    @Inject(API_SERVICE_CONFIG) private config: IApiServiceConfig,
  ) {}

  getAll(workOrderId: string): Observable<WorkOrderAction[]> {
    return this.http.get<WorkOrderAction[]>(
      `${this.config.url}tickets/multimachinesupports/${workOrderId}/actions`,
    );
  }

  create(
    workOrderId: string,
    action: WorkOrderActionInput,
  ): Observable<string> {
    return this.http
      .post<{
        id: string;
      }>(
        `${this.config.url}tickets/multimachinesupports/${workOrderId}/actions`,
        action,
      )
      .pipe(map((res) => res.id));
  }

  update(
    workOrderId: string,
    actionId: string,
    action: WorkOrderActionInput,
  ): Observable<void> {
    return this.http.patch<void>(
      `${this.config.url}tickets/multimachinesupports/${workOrderId}/actions/${actionId}`,
      action,
    );
  }

  attach(
    workOrderId: string,
    actionId: string,
    attachments: File[],
  ): Observable<void> {
    const fd = new FormData();
    for (const a of attachments) {
      fd.append('file', a);
    }
    return this.http.post<void>(
      `${this.config.url}tickets/multimachinesupports/${workOrderId}/actions/${actionId}/attachments`,
      fd,
    );
  }

  delete(workOrderId: string, actionId: string) {
    return this.http.delete<void>(
      `${this.config.url}tickets/multimachinesupports/${workOrderId}/actions/${actionId}`,
    );
  }

  getDeletionImpact(
    workOrderId: string,
    actionId: string,
  ): Observable<ActionDeletionImpact> {
    return this.http.get<ActionDeletionImpact>(
      `${this.config.url}tickets/multimachinesupports/${workOrderId}/actions/${actionId}/deletion-impact`,
    );
  }
}

/**
 * Whether deleting an action would un-assign its equipment. The rule spans work
 * orders, so only the back end can answer it.
 */
export interface ActionDeletionImpact {
  releasesEquipment: boolean;
}

export function isAssetAlreadyOwnedError(err: HttpErrorResponse): boolean {
  return isSingleErrorCode(err, ERROR_ASSET_ALREADY_OWNED);
}

export const ERROR_ASSET_ALREADY_OWNED = 'ASSET_ALREADY_OWNED';

export function isInstallationRequiresUnassignedEquipmentError(
  err: HttpErrorResponse,
): boolean {
  return isSingleErrorCode(err, ERROR_INSTALLATION_REQUIRES_UNASSIGNED_ASSET);
}

export const ERROR_INSTALLATION_REQUIRES_UNASSIGNED_ASSET =
  'INSTALLATION_REQUIRES_UNASSIGNED_ASSET';
